import { router } from "../router/router.js";
import { contextStore, getRoleDisplayName } from "../auth/context-store.js";
import { platform } from "../../app/browser-platform.js";
import type { User } from "../../domain/identity/user.js";
import { UserStatus } from "../../domain/identity/user.js";
import { userInitials, validateProfileImage } from "../../domain/identity/user-avatar.js";
import type { Client } from "../../domain/client/client.js";
import { BUILT_IN_ROLE_PERMISSIONS } from "../../permissions/built-in-role-policy.js";
import type { BuiltInRoleCode } from "../../domain/identity/access-types.js";
import { PermissionCode } from "../../permissions/permissions.js";

export default class AdminUsersPage extends HTMLElement {
  private users: User[] = [];
  private clients: Client[] = [];
  private professionalNameByUserId: Map<string, string> = new Map();
  private searchQuery = "";
  private statusFilter = "ALL";
  private isModalOpen = false;
  private editingUser: User | null = null;
  /** userId → photo data URL. The photo is the user's avatar, never a professional's signature. */
  private userPhotos: Map<string, string> = new Map();
  /** Photo chosen in the open modal but not saved yet; null means "remove the current one". */
  private pendingPhoto: { dataUrl: string; fileName: string; mimeType: string; sizeBytes: number } | null = null;
  private pendingPhotoRemoval = false;
  private photoError: string | null = null;
  private isPasswordModalOpen = false;
  private passwordTargetUser: User | null = null;
  private showPasswordInModal = false;
  private showQuickPassword = false;
  private deleteModal = { isOpen: false, id: "", title: "", message: "", error: "", userName: "", userProfile: "" };
  private isProfileModalOpen = false;
  private isPermissionsModalOpen = false;
  private targetUser: User | null = null;
  private boundHandleMasterDataUpdated: (e: Event) => void;

  constructor() {
    super();
    this.boundHandleMasterDataUpdated = this.handleMasterDataUpdated.bind(this);
  }

  private handleMasterDataUpdated(e: Event) {
    this.loadData();
  }

  public connectedCallback(): void {
    this.loadData();
    window.addEventListener("active-client-changed", () => this.loadData());
    window.addEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  public disconnectedCallback(): void {
    window.removeEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  private async loadData(): Promise<void> {
    try {
      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;
      const [uList, cList, pList] = await Promise.all([
        platform.adminService.listUsers(dummyContext),
        platform.adminService.listClients(dummyContext),
        platform.adminService.listProfessionals(dummyContext).catch(() => [])
      ]);
      this.users = uList;
      this.clients = cList;
      this.professionalNameByUserId = new Map(
        (pList as any[]).filter(p => p.userId).map(p => [p.userId as string, p.name as string])
      );

      // Each photo is fetched by its own userId — never by name, e-mail or
      // position in the list.
      const photos = await Promise.all(
        this.users.map(async u => {
          const dataUrl = await platform.adminService.getUserProfileImage(dummyContext, u.id).catch(() => null);
          return [u.id as string, dataUrl] as const;
        })
      );
      this.userPhotos = new Map(photos.filter(([, dataUrl]) => !!dataUrl) as Array<[string, string]>);

      this.render();
    } catch (e) {
      console.error("Failed to load users", e);
    }
  }

  /**
   * Circular avatar: the registered photo when there is one, the user's
   * initials otherwise. Never renders a broken image, "null" or "undefined",
   * and `object-fit: cover` keeps the photo from being stretched.
   */
  private renderAvatar(user: User, size: number): string {
    const photo = this.userPhotos.get(user.id);
    const common = `width:${size}px; height:${size}px; border-radius:50%; flex-shrink:0;`;

    if (photo) {
      return `<img src="${photo}" alt="Foto de ${this.escapeHtml(user.name)}" style="${common} object-fit: cover; border:1px solid var(--color-border-subtle);">`;
    }
    return `
      <div style="${common} background: var(--color-surface-secondary); color: var(--color-ink); display:flex; align-items:center; justify-content:center; font-size:${Math.round(size * 0.36)}px; font-weight:700;">
        ${this.escapeHtml(userInitials(user.name))}
      </div>`;
  }

  private escapeHtml(value: string | null | undefined): string {
    return (value || "")
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#039;");
  }

  private render(): void {
    const ctxPermissions = contextStore.get()?.permissions;
    const canDeleteUsers = ctxPermissions?.has(PermissionCode.UserDelete) ?? false;
    const currentUserId = contextStore.get()?.userId;

    const filtered = this.users.filter(u => {
      const matchesStatus = this.statusFilter === "ALL" || u.status === this.statusFilter;
      const term = this.searchQuery.toLowerCase();
      const userLogin = (u.login || u.username || "").toLowerCase();
      const matchesSearch = !term ||
        u.name.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term) ||
        userLogin.includes(term);
      return matchesStatus && matchesSearch;
    });

    this.innerHTML = `
      <div style="display: flex; flex-direction: column; height: 100%; gap: 16px; background: var(--color-bg-primary); padding: 24px;">
        <header style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 16px;">
          <div>
            <h1 style="color: var(--color-text-primary); font-size: 1.5rem; font-weight: 700; margin: 0;">Gestão de Usuários & Contas</h1>
            <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin-top: 4px;">Controle de acesso, credenciais (login/senha), autenticação e permissões de usuários.</p>
          </div>
          <button id="btn-new-user" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 10px 18px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 0.875rem; display: flex; align-items: center; gap: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);">
            <svg style="width: 16px; height: 16px;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path></svg>
            <span>+ Novo Usuário</span>
          </button>
        </header>

        <!-- Filters -->
        <div style="display: flex; gap: 12px; background: var(--color-surface-primary); padding: 16px; border-radius: 8px; border: 1px solid var(--color-graphite-200); align-items: center;">
          <input type="text" id="input-search" placeholder="Buscar por nome, login de acesso ou e-mail..." value="${this.searchQuery}" style="flex: 1; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
          <select id="select-status" style="padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
            <option value="ALL" ${this.statusFilter === "ALL" ? "selected" : ""}>Todos os Status</option>
            <option value="ACTIVE" ${this.statusFilter === "ACTIVE" ? "selected" : ""}>Ativos</option>
            <option value="INACTIVE" ${this.statusFilter === "INACTIVE" ? "selected" : ""}>Inativos</option>
          </select>
        </div>

        <!-- Data Table -->
        <div style="flex: 1; background: var(--color-surface-primary); border-radius: 8px; border: 1px solid var(--color-graphite-200); overflow-y: auto;">
          <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.875rem;">
            <thead style="background: var(--color-surface-secondary); border-bottom: 1px solid var(--color-graphite-200); position: sticky; top: 0; z-index: 10;">
              <tr>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">NOME COMPLETO</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">LOGIN (USUÁRIO)</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">E-MAIL DE ACESSO</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">PERFIL</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">PROFISSIONAL VINCULADO</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">SENHA</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">STATUS</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">ÚLTIMO ACESSO</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600; text-align: right;">AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.length === 0 ? `
                <tr>
                  <td colspan="9" style="padding: 36px; text-align: center; color: var(--color-graphite-400);">Nenhum usuário encontrado com os filtros aplicados.</td>
                </tr>
              ` : filtered.map(u => {
                const userLogin = u.login || u.username || u.email.split('@')[0];
                return `
                <tr style="border-bottom: 1px solid var(--color-bg-secondary); transition: background 0.15s; background: var(--color-surface-primary);" onmouseover="this.style.background='var(--color-bg-primary)'" onmouseout="this.style.background='var(--color-surface-primary)'">
                  <td style="padding: 12px 16px; font-weight: 600; color: var(--ia-primary);">
                    <div style="display: flex; align-items: center; gap: 8px;">
                      ${this.renderAvatar(u, 28)}
                      <span>${u.name}</span>
                    </div>
                  </td>
                  <td style="padding: 12px 16px;">
                    <span style="font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; background: var(--color-surface-secondary); color: var(--ia-primary); padding: 3px 8px; border-radius: 4px; font-size: 0.8125rem; font-weight: 600; border: 1px solid var(--color-border-subtle);">
                      ${userLogin}
                    </span>
                  </td>
                  <td style="padding: 12px 16px; color: var(--color-text-secondary);">${u.email}</td>
                  <td style="padding: 12px 16px; color: var(--color-text-secondary); font-size: 0.8125rem;">
                    ${u.profileType ? getRoleDisplayName(u.profileType) : '— (não definido)'}
                  </td>
                  <td style="padding: 12px 16px; color: var(--color-text-secondary); font-size: 0.8125rem;">
                    ${this.professionalNameByUserId.get(u.id) || '—'}
                  </td>
                  <td style="padding: 12px 16px;">
                    <div style="display: flex; align-items: center; gap: 6px;">
                      <span style="font-family: monospace; letter-spacing: 2px; color: var(--color-text-secondary); font-size: 0.875rem;">••••••</span>
                      <button class="btn-quick-password" data-id="${u.id}" title="Alterar ou redefinir senha" style="background: none; border: 1px solid var(--color-graphite-300); border-radius: 4px; padding: 2px 6px; font-size: 0.7rem; color: var(--color-text-secondary); cursor: pointer; display: flex; align-items: center; gap: 4px;">
                        <svg style="width: 12px; height: 12px;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"></path></svg>
                        Redefinir
                      </button>
                    </div>
                  </td>
                  <td style="padding: 12px 16px;">
                    <span style="background: ${u.status === 'ACTIVE' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'}; color: ${u.status === 'ACTIVE' ? 'var(--status-success-solid)' : 'var(--status-danger-solid)'}; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">
                      ${u.status === 'ACTIVE' ? 'ATIVO' : 'INATIVO'}
                    </span>
                  </td>
                  <td style="padding: 12px 16px; color: var(--color-text-secondary); font-size: 0.8125rem;">
                    ${u.lastAccessAt ? new Date(u.lastAccessAt).toLocaleString('pt-BR') : 'Nunca'}
                  </td>
                  <td style="padding: 12px 16px; text-align: right;">
                    <button class="btn-profile" data-id="${u.id}" style="background: var(--ia-action-bg, var(--color-primary)); border: 1px solid var(--color-primary); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--ia-action-fg, #F7F8F8); font-weight: 600; margin-right: 4px;">Perfil</button>
                    <button class="btn-permissions" data-id="${u.id}" style="background: var(--ia-action-bg, var(--color-primary)); border: 1px solid var(--color-primary); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--ia-action-fg, #F7F8F8); font-weight: 600; margin-right: 4px;">Permissões</button>
                    <button class="btn-edit" data-id="${u.id}" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--color-text-muted); margin-right: 4px;">Editar</button>
                    ${u.status === 'ACTIVE' ? `
                      <button class="btn-inactivate" data-id="${u.id}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text); margin-right: 4px;">Inativar</button>
                    ` : ''}
                    ${canDeleteUsers ? `
                      <button class="btn-delete" data-id="${u.id}" data-name="${u.name}" data-profile="${u.profileType ? getRoleDisplayName(u.profileType) : '— (não definido)'}" ${u.id === currentUserId ? 'disabled title="Não é possível excluir o usuário atualmente logado."' : ''} style="background: ${u.id === currentUserId ? 'var(--color-bg-secondary)' : 'var(--status-danger-bg)'}; border: 1px solid ${u.id === currentUserId ? 'var(--color-graphite-300)' : 'var(--status-danger-solid)'}; border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: ${u.id === currentUserId ? 'not-allowed' : 'pointer'}; color: ${u.id === currentUserId ? 'var(--color-graphite-400)' : 'var(--status-danger-text)'};">Excluir</button>
                    ` : ''}
                  </td>
                </tr>
                `;
              }).join("")}
            </tbody>
          </table>
        </div>

        ${this.isModalOpen ? this.renderModal() : ""}
        ${this.isPasswordModalOpen ? this.renderPasswordModal() : ""}
        ${this.deleteModal.isOpen ? this.renderDeleteModal() : ""}
        ${this.isProfileModalOpen ? this.renderProfileModal() : ""}
        ${this.isPermissionsModalOpen ? this.renderPermissionsModal() : ""}
      </div>
    `;

    this.attachEvents();
  }

  /** Current photo as shown by the open modal: pending choice, pending removal, or what is stored. */
  private currentModalPhoto(): string | null {
    if (this.pendingPhotoRemoval) return null;
    if (this.pendingPhoto) return this.pendingPhoto.dataUrl;
    return this.editingUser ? (this.userPhotos.get(this.editingUser.id) || null) : null;
  }

  private renderPhotoSection(): string {
    const photo = this.currentModalPhoto();
    const name = this.editingUser?.name || "";

    const preview = photo
      ? `<img src="${photo}" alt="Pré-visualização da foto" style="width:96px; height:96px; border-radius:8px; object-fit: cover; border:1px solid var(--color-graphite-300);">`
      : `<div style="width:96px; height:96px; border-radius:8px; background: var(--color-surface-secondary); border:1px dashed var(--color-graphite-300); display:flex; align-items:center; justify-content:center; font-size:1.6rem; font-weight:700; color: var(--color-ink);">
           ${this.escapeHtml(userInitials(name))}
         </div>`;

    return `
      <div>
        <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 8px;">Foto do Usuário</label>
        <div style="display:flex; align-items:center; gap:16px;">
          ${preview}
          <div style="display:flex; flex-direction:column; gap:8px;">
            <button type="button" id="btn-photo-select" style="background: var(--color-surface-secondary); border:1px solid var(--color-graphite-300); border-radius:6px; padding:6px 12px; font-size:0.8125rem; cursor:pointer; color: var(--color-text-secondary);">
              ${photo ? "Substituir" : "Selecionar imagem"}
            </button>
            ${photo ? `<button type="button" id="btn-photo-remove" style="background:none; border:1px solid var(--status-danger-solid); color: var(--status-danger-solid); border-radius:6px; padding:6px 12px; font-size:0.8125rem; cursor:pointer;">Remover</button>` : ""}
            <small style="font-size:0.7rem; color: var(--color-graphite-400);">PNG, JPG, JPEG ou WEBP — até 5 MB.</small>
            <input type="file" id="inp-photo-file" accept="image/png,image/jpeg,image/webp" style="display:none;">
          </div>
        </div>
        ${this.photoError ? `<div style="margin-top:8px; font-size:0.78rem; color: var(--status-danger-solid);">${this.escapeHtml(this.photoError)}</div>` : ""}
      </div>
    `;
  }

  private renderModal(): string {
    const isEdit = !!this.editingUser;
    const currentLogin = this.editingUser ? (this.editingUser.login || this.editingUser.username || this.editingUser.email.split('@')[0]) : '';

    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 560px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 1px solid var(--color-bg-secondary); padding-bottom: 12px;">
            <div>
              <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0;">${isEdit ? 'Editar Usuário e Credenciais' : 'Novo Usuário (Login & Senha)'}</h2>
              <p style="font-size: 0.8125rem; color: var(--color-text-secondary); margin: 4px 0 0 0;">Defina os dados pessoais e as credenciais de acesso ao sistema.</p>
            </div>
            <button id="btn-close-modal" style="background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary); line-height: 1;">✕</button>
          </div>

          <form id="form-user" style="display: flex; flex-direction: column; gap: 16px;">
            ${this.renderPhotoSection()}
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Nome Completo *</label>
              <input type="text" id="modal-name" value="${this.editingUser?.name || ''}" required placeholder="Ex: Carlos Silva" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>

            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Login de Acesso (Usuário) *</label>
                <div style="position: relative;">
                  <span style="position: absolute; left: 10px; top: 9px; color: var(--color-graphite-400); font-family: monospace; font-size: 0.875rem;">@</span>
                  <input type="text" id="modal-login" value="${currentLogin}" required placeholder="carlos.silva" style="width: 100%; padding: 8px 12px 8px 28px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; font-family: monospace;">
                </div>
                <span style="font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 2px; display: block;">Identificador único para login</span>
              </div>

              <div>
                <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">E-mail de Acesso *</label>
                <input type="email" id="modal-email" value="${this.editingUser?.email || ''}" required placeholder="carlos.silva@empresa.com" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
                <span style="font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 2px; display: block;">Utilizado para avisos e login alternativo</span>
              </div>
            </div>

            <!-- Senha -->
            <div style="background: var(--color-bg-primary); border: 1px solid var(--color-graphite-200); border-radius: 8px; padding: 14px;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                <label style="font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted);">
                  ${isEdit ? 'Nova Senha de Acesso (Opcional)' : 'Senha de Acesso *'}
                </label>
                <button type="button" id="btn-generate-password" style="background: none; border: 1px dashed var(--color-ink); border-radius: 4px; padding: 2px 8px; font-size: 0.75rem; color: var(--color-ink); cursor: pointer; font-weight: 500;">
                  ⚡ Gerar Senha Segura
                </button>
              </div>

              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
                <div style="position: relative;">
                  <input type="${this.showPasswordInModal ? 'text' : 'password'}" id="modal-password" ${!isEdit ? 'required' : ''} placeholder="${isEdit ? 'Manter senha atual' : 'Mínimo 6 caracteres'}" value="${!isEdit ? '123456' : ''}" style="width: 100%; padding: 8px 36px 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: var(--color-surface-primary);">
                  <button type="button" id="btn-toggle-password-view" style="position: absolute; right: 8px; top: 8px; background: none; border: none; cursor: pointer; color: var(--color-text-secondary); padding: 2px;">
                    ${this.showPasswordInModal ? `
                      <svg style="width: 18px; height: 18px;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18"></path></svg>
                    ` : `
                      <svg style="width: 18px; height: 18px;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                    `}
                  </button>
                </div>

                <div>
                  <input type="${this.showPasswordInModal ? 'text' : 'password'}" id="modal-password-confirm" ${!isEdit ? 'required' : ''} placeholder="Confirmar senha" value="${!isEdit ? '123456' : ''}" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; background: var(--color-surface-primary);">
                </div>
              </div>
              <span style="font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 6px; display: block;">
                ${isEdit ? 'Deixe os campos de senha em branco se não desejar alterá-la.' : 'Dica: Você pode definir uma senha inicial (padrão: 123456) ou gerar uma automática.'}
              </span>
            </div>

            ${!isEdit ? `
              <div>
                <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Tipo de Perfil *</label>
                <select id="modal-role" required style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
                  <option value="DEVELOPER">DESENVOLVEDOR</option>
                  <option value="ADMIN">ADMINISTRADOR</option>
                  <option value="ENGINEER">ENGENHEIRO</option>
                  <option value="INSPECTOR" selected>INSPETOR</option>
                  <option value="DOCUMENTATION">TÉCNICO DE DOCUMENTAÇÃO</option>
                  <option value="VIEWER">VISUALIZADOR</option>
                </select>
                <span style="font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 2px; display: block;">Define as permissões efetivas do usuário. Pode ser alterado depois pelo botão "Perfil" na listagem.</span>
              </div>
            ` : ''}

            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px;">
              <button type="button" id="btn-cancel-modal" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 500; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
              <button type="submit" style="background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; padding: 8px 20px; font-size: 0.875rem; font-weight: 600; color: var(--ia-action-fg, #F7F8F8); cursor: pointer; box-shadow: 0 1px 2px rgba(0,0,0,0.05);">
                ${isEdit ? 'Salvar Alterações' : 'Criar Usuário'}
              </button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  private renderPasswordModal(): string {
    if (!this.passwordTargetUser) return "";
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 440px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
            <h2 style="font-size: 1.15rem; font-weight: 700; color: var(--color-text-muted); margin: 0;">Redefinir Senha de Acesso</h2>
            <button id="btn-close-quick-pwd" style="background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary);">✕</button>
          </div>

          <div style="background: var(--color-bg-primary); border: 1px solid var(--color-graphite-200); border-radius: 6px; padding: 10px 12px; margin-bottom: 16px; font-size: 0.8125rem;">
            <div><strong>Usuário:</strong> ${this.passwordTargetUser.name}</div>
            <div><strong>Login:</strong> <span style="font-family: monospace; color: var(--color-ink);">@${this.passwordTargetUser.login || this.passwordTargetUser.username || this.passwordTargetUser.email.split('@')[0]}</span></div>
          </div>

          <form id="form-quick-password" style="display: flex; flex-direction: column; gap: 14px;">
            <div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                <label style="font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted);">Nova Senha *</label>
                <button type="button" id="btn-quick-generate-pwd" style="background: none; border: none; color: var(--color-ink); font-size: 0.75rem; cursor: pointer; font-weight: 500;">⚡ Gerar aleatória</button>
              </div>
              <div style="position: relative;">
                <input type="${this.showQuickPassword ? 'text' : 'password'}" id="inp-quick-new-pwd" required minlength="4" placeholder="Digite a nova senha" style="width: 100%; padding: 8px 36px 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
                <button type="button" id="btn-toggle-quick-pwd-view" style="position: absolute; right: 8px; top: 8px; background: none; border: none; cursor: pointer; color: var(--color-text-secondary);">
                  ${this.showQuickPassword ? 'Ocultar' : 'Ver'}
                </button>
              </div>
            </div>

            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Confirmar Nova Senha *</label>
              <input type="${this.showQuickPassword ? 'text' : 'password'}" id="inp-quick-confirm-pwd" required minlength="4" placeholder="Repita a nova senha" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px;">
              <button type="button" id="btn-cancel-quick-pwd" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 500; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
              <button type="submit" style="background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; padding: 8px 18px; font-size: 0.875rem; font-weight: 600; color: var(--ia-action-fg, #F7F8F8); cursor: pointer;">Salvar Nova Senha</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  private generateRandomPassword(): string {
    const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$";
    let pwd = "";
    for (let i = 0; i < 8; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return pwd;
  }

  private attachEvents(): void {
    this.querySelector("#input-search")?.addEventListener("input", (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.render();
    });

    this.querySelector("#select-status")?.addEventListener("change", (e) => {
      this.statusFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector("#btn-close-profile")?.addEventListener("click", () => { this.isProfileModalOpen = false; this.render(); });
    this.querySelector("#btn-cancel-profile")?.addEventListener("click", () => { this.isProfileModalOpen = false; this.render(); });
    
    this.querySelector("#btn-save-profile")?.addEventListener("click", async () => {
      if (!this.targetUser) return;
      const select = this.querySelector("#modal-profile-type") as HTMLSelectElement;
      const newProfile = select.value;
      
      try {
        const ctx = contextStore.get();
        const dummyContext = { activeClientId: ctx.activeClientId, permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;

        // Changing the profile always resets custom permission overrides:
        // the user's effective permissions become exactly the new Role's
        // permissions (BUILT_IN_ROLE_PERMISSIONS), recalculated fresh. Any
        // extra permissions must be granted again explicitly via "Editar
        // Permissões" — they are additions on top of the Role, never a
        // replacement of it (see src/permissions/effective-permissions.ts).
        await platform.adminService.updateUser(dummyContext, {
          ...this.targetUser,
          profileType: newProfile,
          customPermissions: undefined
        } as any);

        this.isProfileModalOpen = false;
        this.targetUser = null;
        await this.loadData();
      } catch (err: any) {
        alert(err.message || "Erro ao atualizar perfil");
      }
    });

    this.querySelector("#btn-close-permissions")?.addEventListener("click", () => { this.isPermissionsModalOpen = false; this.render(); });
    this.querySelector("#btn-cancel-permissions")?.addEventListener("click", () => { this.isPermissionsModalOpen = false; this.render(); });
    
    this.querySelector("#btn-restore-permissions")?.addEventListener("click", async () => {
      if (!this.targetUser) return;
      try {
        const ctx = contextStore.get();
        const dummyContext = { activeClientId: ctx.activeClientId, permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
        
        await platform.adminService.updateUser(dummyContext, {
          ...this.targetUser,
          customPermissions: undefined
        } as any);
        
        this.isPermissionsModalOpen = false;
        this.targetUser = null;
        await this.loadData();
      } catch (err: any) {
        alert(err.message || "Erro ao restaurar permissões");
      }
    });

    this.querySelector("#btn-save-permissions")?.addEventListener("click", async () => {
      if (!this.targetUser) return;
      
      // Only checked-and-not-disabled boxes represent a manual grant beyond
      // the Role; disabled ones are already inherited from the Role and must
      // not be duplicated into customPermissions.
      const checkboxes = this.querySelectorAll(".perm-checkbox:checked:not(:disabled)");
      const customPermissions = Array.from(checkboxes).map(cb => (cb as HTMLInputElement).value);
      
      try {
        const ctx = contextStore.get();
        const dummyContext = { activeClientId: ctx.activeClientId, permissions: ctx.permissions, correlationId: "", userId: ctx.userId } as any;
        
        await platform.adminService.updateUser(dummyContext, {
          ...this.targetUser,
          customPermissions
        } as any);
        
        this.isPermissionsModalOpen = false;
        this.targetUser = null;
        await this.loadData();
      } catch (err: any) {
        alert(err.message || "Erro ao atualizar permissões");
      }
    });

    this.querySelector("#btn-new-user")?.addEventListener("click", () => {
      this.editingUser = null;
      this.showPasswordInModal = false;
      this.isModalOpen = true;
      this.render();
    });

    this.querySelectorAll(".btn-profile").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.target as HTMLElement).getAttribute("data-id")!;
        this.targetUser = this.users.find(u => u.id === id) || null;
        this.isProfileModalOpen = true;
        this.render();
      });
    });

    this.querySelectorAll(".btn-permissions").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.target as HTMLElement).getAttribute("data-id")!;
        this.targetUser = this.users.find(u => u.id === id) || null;
        this.isPermissionsModalOpen = true;
        this.render();
      });
    });

    this.querySelectorAll(".btn-edit").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        this.editingUser = this.users.find(u => u.id === id) || null;
        this.showPasswordInModal = false;
        this.isModalOpen = true;
        this.render();
      });
    });

    this.querySelectorAll(".btn-quick-password").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        this.passwordTargetUser = this.users.find(u => u.id === id) || null;
        this.showQuickPassword = false;
        this.isPasswordModalOpen = true;
        this.render();
      });
    });

    // Auto-preencher login quando digita o email se o login estiver vazio
    this.querySelector("#modal-email")?.addEventListener("input", (e) => {
      const emailVal = (e.target as HTMLInputElement).value;
      const loginInp = this.querySelector("#modal-login") as HTMLInputElement;
      if (loginInp && (!loginInp.value || !loginInp.dataset['manuallyEdited'])) {
        const prefix = emailVal.split('@')[0] || '';
        const generated = prefix.toLowerCase().replace(/\s+/g, '.');
        loginInp.value = generated;
      }
    });

    this.querySelector("#modal-login")?.addEventListener("input", (e) => {
      (e.target as HTMLInputElement).dataset['manuallyEdited'] = "true";
    });

    // Gerador de senha no modal principal
    this.querySelector("#btn-generate-password")?.addEventListener("click", () => {
      const generated = this.generateRandomPassword();
      const pwdInp = this.querySelector("#modal-password") as HTMLInputElement;
      const confirmInp = this.querySelector("#modal-password-confirm") as HTMLInputElement;
      if (pwdInp && confirmInp) {
        pwdInp.value = generated;
        confirmInp.value = generated;
        this.showPasswordInModal = true;
        pwdInp.type = "text";
        confirmInp.type = "text";
      }
    });

    // Toggle exibir/ocultar senha no modal principal
    this.querySelector("#btn-toggle-password-view")?.addEventListener("click", () => {
      this.showPasswordInModal = !this.showPasswordInModal;
      const pwdInp = this.querySelector("#modal-password") as HTMLInputElement;
      const confirmInp = this.querySelector("#modal-password-confirm") as HTMLInputElement;
      if (pwdInp) pwdInp.type = this.showPasswordInModal ? "text" : "password";
      if (confirmInp) confirmInp.type = this.showPasswordInModal ? "text" : "password";
    });

    // Quick password modal handlers
    this.querySelector("#btn-close-quick-pwd")?.addEventListener("click", () => {
      this.isPasswordModalOpen = false;
      this.passwordTargetUser = null;
      this.render();
    });

    this.querySelector("#btn-cancel-quick-pwd")?.addEventListener("click", () => {
      this.isPasswordModalOpen = false;
      this.passwordTargetUser = null;
      this.render();
    });

    this.querySelector("#btn-quick-generate-pwd")?.addEventListener("click", () => {
      const gen = this.generateRandomPassword();
      const p1 = this.querySelector("#inp-quick-new-pwd") as HTMLInputElement;
      const p2 = this.querySelector("#inp-quick-confirm-pwd") as HTMLInputElement;
      if (p1 && p2) {
        p1.value = gen;
        p2.value = gen;
        this.showQuickPassword = true;
        p1.type = "text";
        p2.type = "text";
      }
    });

    this.querySelector("#btn-toggle-quick-pwd-view")?.addEventListener("click", () => {
      this.showQuickPassword = !this.showQuickPassword;
      const p1 = this.querySelector("#inp-quick-new-pwd") as HTMLInputElement;
      const p2 = this.querySelector("#inp-quick-confirm-pwd") as HTMLInputElement;
      if (p1) p1.type = this.showQuickPassword ? "text" : "password";
      if (p2) p2.type = this.showQuickPassword ? "text" : "password";
      const toggleBtn = this.querySelector("#btn-toggle-quick-pwd-view");
      if (toggleBtn) toggleBtn.textContent = this.showQuickPassword ? "Ocultar" : "Ver";
    });

    this.querySelector("#form-quick-password")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (!this.passwordTargetUser) return;
      const p1 = (this.querySelector("#inp-quick-new-pwd") as HTMLInputElement).value;
      const p2 = (this.querySelector("#inp-quick-confirm-pwd") as HTMLInputElement).value;

      if (p1 !== p2) {
        alert("A nova senha e a confirmação não coincidem!");
        return;
      }

      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      try {
        await platform.adminService.updateUser(dummyContext, {
          ...this.passwordTargetUser,
          password: p1
        });
        alert(`Senha do usuário '${this.passwordTargetUser.name}' alterada com sucesso!`);
        this.isPasswordModalOpen = false;
        this.passwordTargetUser = null;
        await this.loadData();
      } catch (err: any) {
        alert(err.message || "Erro ao redefinir senha.");
      }
    });

    this.querySelectorAll(".btn-inactivate").forEach(btn => {
      btn.addEventListener("click", async (e) => {
        const target = e.currentTarget as HTMLElement;
        const id = target.getAttribute("data-id");
        if (!id) return;
        this.deleteModal = {
          isOpen: true,
          id,
          title: "Inativar Usuário",
          message: "Deseja realmente inativar o acesso deste usuário?",
          error: "",
          userName: "",
          userProfile: ""
        };
        this.render();
      });
    });

    this.querySelectorAll(".btn-delete").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const target = e.currentTarget as HTMLElement;
        const id = target.getAttribute("data-id");
        if (!id) return;
        this.deleteModal = {
          isOpen: true,
          id,
          title: "Excluir Usuário",
          message: "Esta ação não poderá ser desfeita. Se o usuário possuir histórico preservado (vínculo profissional ou autoria de análise/validação/aprovação de Minuta), ele será apenas desativado em vez de excluído.",
          error: "",
          userName: target.getAttribute("data-name") || "",
          userProfile: target.getAttribute("data-profile") || ""
        };
        this.render();
      });
    });

    this.querySelector("#btn-close-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });

    this.querySelector("#btn-cancel-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });

    const fileInput = this.querySelector("#inp-photo-file") as HTMLInputElement | null;
    this.querySelector("#btn-photo-select")?.addEventListener("click", () => fileInput?.click());
    this.querySelector("#btn-photo-remove")?.addEventListener("click", () => {
      if (!confirm("Deseja remover a foto deste usuário?")) return;
      // Removes only the photo — the user is untouched.
      this.pendingPhoto = null;
      this.pendingPhotoRemoval = true;
      this.photoError = null;
      this.render();
    });
    fileInput?.addEventListener("change", async () => {
      const file = fileInput.files?.[0];
      if (!file) return;

      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const candidate = { dataUrl, fileName: file.name, mimeType: file.type, sizeBytes: file.size };
      const errors = validateProfileImage(candidate);
      if (errors.length > 0) {
        this.photoError = errors.join(" ");
        this.pendingPhoto = null;
      } else {
        this.photoError = null;
        this.pendingPhoto = candidate;
        this.pendingPhotoRemoval = false;
      }
      fileInput.value = "";
      this.render();
    });

    this.querySelector("#form-user")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const name = (this.querySelector("#modal-name") as HTMLInputElement).value;
      const login = (this.querySelector("#modal-login") as HTMLInputElement).value.trim();
      const email = (this.querySelector("#modal-email") as HTMLInputElement).value.trim();
      const password = (this.querySelector("#modal-password") as HTMLInputElement)?.value || "";
      const confirmPassword = (this.querySelector("#modal-password-confirm") as HTMLInputElement)?.value || "";
      const role = this.querySelector("#modal-role") ? (this.querySelector("#modal-role") as HTMLSelectElement).value : "INSPECTOR";

      if (password && password !== confirmPassword) {
        alert("A senha e a confirmação de senha não coincidem.");
        return;
      }

      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      try {
        let targetUserId: string;
        if (this.editingUser) {
          const updated = await platform.adminService.updateUser(dummyContext, {
            ...this.editingUser,
            name,
            login,
            email,
            ...(password ? { password } : {})
          });
          targetUserId = updated?.id || this.editingUser.id;
        } else {
          const created = await platform.adminService.createUser(dummyContext, {
            name,
            login,
            email,
            password: password || '123456',
            roleCodes: [role],
            profileType: role
          });
          targetUserId = created.id;
        }

        // The photo is bound to this user's id, always after the user exists.
        if (this.pendingPhoto) {
          await platform.adminService.setUserProfileImage(dummyContext, targetUserId, this.pendingPhoto);
        } else if (this.pendingPhotoRemoval) {
          await platform.adminService.removeUserProfileImage(dummyContext, targetUserId);
        }

        this.pendingPhoto = null;
        this.pendingPhotoRemoval = false;
        this.photoError = null;
        this.isModalOpen = false;
        await this.loadData();
      } catch (err: any) {
        alert(err.message || "Erro ao salvar usuário.");
      }
    });

    this.querySelector("#form-delete")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const { id, title } = this.deleteModal;
      const isInactivate = title.includes("Inativar");
      
      const passwordAttempt = (this.querySelector("#modal-delete-password") as HTMLInputElement)?.value || "";
      const reason = (this.querySelector("#modal-delete-reason") as HTMLInputElement)?.value || "";

      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId || "",
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      try {
        if (isInactivate) {
          await platform.adminService.inactivateUser(dummyContext, id);
          this.deleteModal.isOpen = false;
          await this.loadData();
        } else {
          if ((platform.adminService as any).requestProtectedDeletion) {
             const res = await (platform.adminService as any).requestProtectedDeletion(dummyContext, "USER", id, passwordAttempt, reason);
             if (res.success) {
               this.deleteModal.isOpen = false;
               await this.loadData();
               // Surface the real outcome (Bloco 8): a soft delete due to
               // preserved history is a materially different result from a
               // physical deletion and must never be silently equated with
               // "excluído" in the UI.
               if (res.resultType === 'SOFT_DELETE') {
                 alert(res.message || "Usuário possui histórico preservado e foi apenas desativado (não excluído fisicamente).");
               } else {
                 alert("Usuário excluído com sucesso.");
               }
             } else {
               this.deleteModal.error = res.message || "Erro na exclusão protegida.";
               this.render();
             }
          } else {
             await platform.adminService.deleteUser(dummyContext, id);
             this.deleteModal.isOpen = false;
             await this.loadData();
          }
        }
      } catch (e: any) {
        this.deleteModal.error = e.message || "Erro ao executar ação.";
        this.render();
      }
    });

    this.querySelector("#btn-cancel-delete")?.addEventListener("click", () => {
      this.deleteModal.isOpen = false;
      this.render();
    });
  }

  private renderDeleteModal(): string {
    const { title, message, error, userName, userProfile } = this.deleteModal;
    const isInactivate = title.includes("Inativar");
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 450px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); text-align: left;">
          <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0 0 16px 0; text-align: center;">${title}</h2>
          ${userName ? `
            <div style="background: var(--color-bg-secondary); border-radius: 6px; padding: 10px 14px; margin-bottom: 12px; font-size: 0.875rem;">
              <div><strong>Usuário:</strong> ${userName}</div>
              <div><strong>Perfil:</strong> ${userProfile || '—'}</div>
            </div>
          ` : ''}
          <p style="color: var(--color-text-secondary); font-size: 0.95rem; margin-bottom: 24px; text-align: center;">${message}</p>
          ${error ? `<div style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 6px; padding: 10px 14px; font-size: 0.8125rem; color: var(--status-danger-text); margin-bottom: 16px;">${error}</div>` : ''}
          
          <form id="form-delete" style="display: flex; flex-direction: column; gap: 16px;">
            ${!isInactivate ? `
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Senha do Administrador *</label>
              <input type="password" id="modal-delete-password" required placeholder="Digite sua senha atual" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Motivo *</label>
              <input type="text" id="modal-delete-reason" required placeholder="Motivo da exclusão" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>
            ` : ''}
            
            <div style="display: flex; justify-content: center; gap: 12px; margin-top: 8px;">
              <button type="button" id="btn-cancel-delete" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 24px; font-size: 0.875rem; font-weight: 600; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
              <button type="submit" style="background: var(--status-danger-solid); border: none; border-radius: 6px; padding: 8px 24px; font-size: 0.875rem; font-weight: 600; color: var(--color-text-primary); cursor: pointer;">Confirmar</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  private renderProfileModal(): string {
    const user = this.targetUser;
    if (!user) return "";
    // No silent "TECHNICIAN" fallback for a missing profileType (a data
    // gap) — pre-select the lowest-privilege role instead (Bloco 5).
    const profile = user.profileType || "VIEWER";
    
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 460px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 1px solid var(--color-bg-secondary); padding-bottom: 12px;">
            <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0;">Alterar Tipo de Perfil</h2>
            <button id="btn-close-profile" style="background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary);">✕</button>
          </div>
          
          <div style="margin-bottom: 24px;">
            <p style="margin: 0 0 16px 0; font-size: 0.875rem; color: var(--color-text-secondary);">
              <strong>Usuário:</strong> ${user.name}
            </p>
            <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Selecione o novo perfil:</label>
            <select id="modal-profile-type" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
              <option value="DEVELOPER" ${profile === 'DEVELOPER' ? 'selected' : ''}>DESENVOLVEDOR</option>
              <option value="ADMIN" ${profile === 'ADMIN' ? 'selected' : ''}>ADMINISTRADOR</option>
              <option value="ENGINEER" ${profile === 'ENGINEER' ? 'selected' : ''}>ENGENHEIRO</option>
              <option value="INSPECTOR" ${profile === 'INSPECTOR' || profile === 'TECHNICIAN' ? 'selected' : ''}>INSPETOR</option>
              <option value="DOCUMENTATION" ${profile === 'DOCUMENTATION' || profile === 'DOCUMENT_TECH' ? 'selected' : ''}>TÉCNICO DE DOCUMENTAÇÃO</option>
              <option value="VIEWER" ${profile === 'VIEWER' ? 'selected' : ''}>VISUALIZADOR</option>
            </select>
          </div>

          <div style="display: flex; justify-content: flex-end; gap: 12px;">
            <button id="btn-cancel-profile" style="padding: 10px 16px; background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; cursor: pointer; color: var(--color-text-muted); font-weight: 600;">Cancelar</button>
            <button id="btn-save-profile" style="padding: 10px 16px; background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; cursor: pointer; color: var(--ia-action-fg, #F7F8F8); font-weight: 600;">Confirmar Alteração</button>
          </div>
        </div>
      </div>
    `;
  }

  private renderPermissionsModal(): string {
    const user = this.targetUser;
    if (!user) return "";
    
    const overrides = user.customPermissions || [];
    
    const modules = [
      { id: 'engineering.view_workspace', label: 'Visualizar Workspace de Engenharia' },
      { id: 'engineering.start_analysis', label: 'Iniciar Análise' },
      { id: 'engineering.edit_recommendation', label: 'Editar Recomendação' },
      { id: 'engineering.request_correction', label: 'Solicitar Correção' },
      { id: 'engineering.delegate_to_inspector', label: 'Enviar ao Inspetor' },
      { id: 'engineering.validate_correction', label: 'Validar Correção' },
      { id: 'engineering.complete_analysis', label: 'Concluir Análise' },
      { id: 'engineering.approve_minuta', label: 'Aprovar Minuta' },
      { id: 'engineering.sign_minuta', label: 'Assinar Minuta como PLH' }
    ];

    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 600px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1); max-height: 90vh; overflow-y: auto;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 1px solid var(--color-bg-secondary); padding-bottom: 12px;">
            <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0;">Editar Permissionamento</h2>
            <button id="btn-close-permissions" style="background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary);">✕</button>
          </div>
          
          <div style="margin-bottom: 24px;">
            <p style="margin: 0 0 16px 0; font-size: 0.875rem; color: var(--color-text-secondary);">
              <strong>Usuário:</strong> ${user.name}<br>
              <strong>Perfil Base:</strong> ${getRoleDisplayName(user.profileType)}
            </p>
            <p style="margin: 0 0 16px 0; font-size: 0.75rem; color: var(--color-text-muted);">
              Permissões marcadas e desabilitadas já vêm do Perfil Base (${getRoleDisplayName(user.profileType)}) e não podem ser removidas aqui — troque o Perfil para alterá-las. Marcar uma permissão desmarcada concede um acesso adicional específico para este usuário, além do que o perfil já garante.
            </p>

            <h3 style="font-size: 1rem; color: var(--color-text-primary); margin-bottom: 12px;">Módulo: ENGENHARIA</h3>
            <div style="display: flex; flex-direction: column; gap: 8px;">
              ${modules.map(mod => {
                const rolePermissions = BUILT_IN_ROLE_PERMISSIONS[(user.profileType || '').toUpperCase() as BuiltInRoleCode] || [];
                const inheritedFromRole = (rolePermissions as readonly string[]).includes(mod.id);
                const checked = inheritedFromRole || overrides.includes(mod.id);
                return `
                <label style="display: flex; align-items: center; gap: 8px; font-size: 0.875rem; color: ${inheritedFromRole ? 'var(--color-text-muted)' : 'var(--color-text-secondary)'};">
                  <input type="checkbox" class="perm-checkbox" value="${mod.id}" ${checked ? 'checked' : ''} ${inheritedFromRole ? 'disabled' : ''}>
                  ${mod.label}${inheritedFromRole ? ' <em>(herdado do perfil)</em>' : ''}
                </label>
              `;
              }).join('')}
            </div>
          </div>

          <div style="display: flex; justify-content: space-between; gap: 12px;">
            <button id="btn-restore-permissions" style="padding: 10px 16px; background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; cursor: pointer; color: var(--status-danger-solid); font-weight: 600;">Restaurar Padrão</button>
            <div style="display: flex; gap: 12px;">
              <button id="btn-cancel-permissions" style="padding: 10px 16px; background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; cursor: pointer; color: var(--color-text-muted); font-weight: 600;">Cancelar</button>
              <button id="btn-save-permissions" style="padding: 10px 16px; background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; cursor: pointer; color: var(--ia-action-fg, #F7F8F8); font-weight: 600;">Salvar</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get("app-admin-users-page")) {
  customElements.define("app-admin-users-page", AdminUsersPage);
}



